import React, { useState } from "react";
import dayjs from "dayjs";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import AdministrationHeader from "../../../components/header-components/AdministrationHeader.jsx";
import Tooltip from "../../../components/form-components/Tooltip.jsx";
import { useForecastPredictability, DEFAULT_HORIZON } from "./useForecastPredictability";
import { buildForecastTiles } from "./format";
import { HeaderPortal } from "../../../components/ui-components/HeaderSlot";
import FreshnessLabel from "../../../components/ui-components/FreshnessLabel";
import ForecastChart from "./ForecastChart";
import ControlKpiTile from "../ControlKpiTile";
import ExecutiveSummary from "../ExecutiveSummary";
import DriverBridge from "./DriverBridge";
import RecentAnomaliesPanel from "../RecentAnomaliesPanel";

// ForecastPredictabilityPage answers one question: "what will we spend, and how sure are we?".
const ForecastPredictabilityPage = () => {
  const [horizon, setHorizon] = useState(DEFAULT_HORIZON);
  const { data, isLoading, isError } = useForecastPredictability(horizon);

  let body;
  if (isLoading) {
    body = (
      <div data-testid="fp-loading" className="mx-auto max-w-screen-2xl p-6" style={{ width: "100%" }}>
        <div className="h-20 animate-pulse rounded-card bg-surface-2" />
        <div className="mt-3 grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-card bg-surface-2" />
          ))}
        </div>
        <div className="mt-3 h-60 animate-pulse rounded-card bg-surface-2" />
      </div>
    );
  } else if (isError || !data) {
    body = (
      <div className="mx-auto max-w-screen-2xl p-6" style={{ width: "100%" }}>
        <div className="rounded-card border border-danger bg-danger-soft px-5 py-4 text-sm text-danger">
          We couldn&apos;t load the forecast right now. Please try again shortly.
        </div>
      </div>
    );
  } else {
    const tiles = buildForecastTiles(data.kpis, data.horizon || horizon, data.forecast_change);
    const wb = data.kpis && data.kpis.budget_risk && data.kpis.budget_risk.worst_budget;
    // Reserve the full-red wash for a genuinely imminent breach (≤7 days); a moderate or distant
    // risk reads calm. Mirrors the Ownership banner.
    const severe = data.verdict && data.verdict.status === "red" && wb && wb.days_to_breach != null && wb.days_to_breach <= 7;
    const asOfLabel = data.as_of ? dayjs(data.as_of).format("D MMM YYYY") : null;
    // Graduated like the sibling pages: amber "Needs attention" by default; red "Action needed"
    // only for a genuinely severe (imminent-breach) state. The verdict is folded into the summary.
    const vstatus = (data.verdict && data.verdict.status) || "green";
    const summaryStatus = vstatus === "green" ? "green" : vstatus === "red" && severe ? "red" : "at_risk";
    // The summary's attention eyebrow already carries the severity word ("Action needed" / "Needs
    // attention"), so strip the verdict's leading "<Label> — " prefix to avoid saying it twice.
    const rawHeadline = data.verdict && data.verdict.headline;
    const headline = rawHeadline ? rawHeadline.replace(/^[^—]+—\s*/, "") : rawHeadline;
    // width:100% inline (w-full is a no-op here) + max-w-screen-2xl to match the Financial Control
    // and Ownership & Accountability sibling pages.
    body = (
      <div className="mx-auto max-w-screen-2xl space-y-3 p-6" style={{ width: "100%" }}>
        {/* As-of stamp lives on row 2 (matching the sibling pages / cost analyzer). */}
        <HeaderPortal slot="row2-right">
          {asOfLabel && <FreshnessLabel>as of {asOfLabel}</FreshnessLabel>}
        </HeaderPortal>
        {/* The verdict is folded into the executive summary: headline as the bold lead, narrative below. */}
        <ExecutiveSummary
          prose={data.summary_prose}
          bullets={data.summary_bullets || []}
          overall={{ status: summaryStatus, headline }}
        />
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {tiles.map((t) => (
            <ControlKpiTile key={t.key} tile={t} />
          ))}
        </div>
        <ForecastChart series={data.series} horizon={horizon} onHorizonChange={setHorizon} showLabLink />
        <DriverBridge bridge={data.bridge} composition={data.composition} />
        {/* Forecast-band-breach anomalies (with acknowledge/triage) live here, beside the
            chart that gives a breach its visual context — not on the executive CFO page. */}
        <RecentAnomaliesPanel />
      </div>
    );
  }

  return (
    <>
      <AdministrationHeader
        title="Economic Control"
        className="text-text-3"
        icon={icon({ name: "display-chart-up", style: "light" })}
        filters={[]}
        showPeriodSelector={false}
      />
      {body}
      {/* react-tooltip instance for the KPI-tile info anchors on this page. */}
      <Tooltip />
    </>
  );
};

export default ForecastPredictabilityPage;
